import React, { useCallback, useContext, useMemo, useState } from 'react';

import Loading from 'components/Loading';
import Typography from 'kingpin/atoms/Typography';
import GqlClientContext from 'contexts/GqlClientContext';
import usePerformanceSlideOutFilterInput from 'components/PerformanceBoards/hooks/usePerformanceSlideOutFilterInput';
import useLockedDebouncedEffect from 'hooks/useLockedDebouncedEffect';

import fetchRecords from '../RelatedDocuments/fetchRecords';
import { RelatedDocument } from '../RelatedDocuments/RelatedDocuments';

/**
 * Renders one dataset's worth of records for a Related Metric Documents section:
 * an optional heading (shown when the metric spans multiple datasets) plus the
 * document cards. Fetches per-dataset because `fetchRecords` targets a single
 * dataType. Filters = driver + default dataset filter (from
 * usePerformanceSlideOutFilterInput) + the metric's own filters.
 */
const MetricDocumentGroup = ({
  dataType,
  heading,
  showHeading,
  fields,
  dateScope,
  metricFilters,
}: {
  dataType: string;
  heading?: string;
  showHeading: boolean;
  fields: string[];
  dateScope: DateRangeInput | undefined;
  metricFilters: FilterInput[];
}) => {
  const { client } = useContext(GqlClientContext);
  const performanceFilter = usePerformanceSlideOutFilterInput({
    datasetForDefaultFiltering: dataType,
  });

  const [documents, setDocuments] = useState<ElasticDocument[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const filters = useMemo(() => {
    const fi: FilterInput[] = [];
    if (performanceFilter) {
      fi.push(performanceFilter);
    }
    metricFilters.forEach((f) => fi.push(f));
    return fi;
  }, [performanceFilter, metricFilters]);

  const args: RelatedDocumentsTypes.FetchDocumentsArgs | undefined =
    useMemo(() => {
      if (!dateScope) {
        return undefined;
      }
      return { dataType, filters, dateScope, client };
    }, [client, dataType, dateScope, filters]);

  const getDocuments = useCallback(
    async (a: RelatedDocumentsTypes.FetchDocumentsArgs | undefined) => {
      if (!a) {
        return [];
      }
      setIsLoading(true);
      return fetchRecords(a);
    },
    [],
  );

  const responseHandler = useCallback((docs: ElasticDocument[]) => {
    setDocuments(docs);
    setIsLoading(false);
  }, []);

  useLockedDebouncedEffect({ args, callback: getDocuments, responseHandler });

  return (
    <div style={{ marginBottom: 16 }}>
      {showHeading && heading && (
        <div style={{ marginBottom: 8 }}>
          <Typography.Body type="Label">{heading}</Typography.Body>
        </div>
      )}
      {isLoading ? (
        <Loading />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {documents.map((d) => (
            <RelatedDocument
              key={d.id}
              elasticDocument={d}
              dataType={dataType}
              fields={fields}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MetricDocumentGroup;
